/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `dive_summary` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class DiveSummaryMesg : Mesg {
    public constructor() : super("DiveSummary", Profile.MesgNum.DIVE_SUMMARY)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp, in s. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** reference_mesg. */
    public var referenceMesg: MesgNum?
        get() = (getFieldValue(REFERENCE_MESG_FIELD_NUM) as? UShort)?.let { MesgNum.fromValue(it) }
        set(value) = setFieldValue(REFERENCE_MESG_FIELD_NUM, value?.value)

    /** reference_index. */
    public var referenceIndex: UShort?
        get() = getFieldValue(REFERENCE_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(REFERENCE_INDEX_FIELD_NUM, value)

    /** avg_depth, in m. 0 if above water */
    public var avgDepth: Double?
        get() = getFieldValue(AVG_DEPTH_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_DEPTH_FIELD_NUM, value)

    /** max_depth, in m. 0 if above water */
    public var maxDepth: Double?
        get() = getFieldValue(MAX_DEPTH_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_DEPTH_FIELD_NUM, value)

    /** surface_interval, in s. Time since end of last dive */
    public var surfaceInterval: UInt?
        get() = getFieldValue(SURFACE_INTERVAL_FIELD_NUM) as? UInt
        set(value) = setFieldValue(SURFACE_INTERVAL_FIELD_NUM, value)

    /** start_cns, in percent. */
    public var startCns: UByte?
        get() = getFieldValue(START_CNS_FIELD_NUM) as? UByte
        set(value) = setFieldValue(START_CNS_FIELD_NUM, value)

    /** end_cns, in percent. */
    public var endCns: UByte?
        get() = getFieldValue(END_CNS_FIELD_NUM) as? UByte
        set(value) = setFieldValue(END_CNS_FIELD_NUM, value)

    /** start_n2, in percent. */
    public var startN2: UShort?
        get() = getFieldValue(START_N2_FIELD_NUM) as? UShort
        set(value) = setFieldValue(START_N2_FIELD_NUM, value)

    /** end_n2, in percent. */
    public var endN2: UShort?
        get() = getFieldValue(END_N2_FIELD_NUM) as? UShort
        set(value) = setFieldValue(END_N2_FIELD_NUM, value)

    /** o2_toxicity, in OTUs. */
    public var o2Toxicity: UShort?
        get() = getFieldValue(O2_TOXICITY_FIELD_NUM) as? UShort
        set(value) = setFieldValue(O2_TOXICITY_FIELD_NUM, value)

    /** dive_number. */
    public var diveNumber: UInt?
        get() = getFieldValue(DIVE_NUMBER_FIELD_NUM) as? UInt
        set(value) = setFieldValue(DIVE_NUMBER_FIELD_NUM, value)

    /** bottom_time, in s. */
    public var bottomTime: Double?
        get() = getFieldValue(BOTTOM_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(BOTTOM_TIME_FIELD_NUM, value)

    /** avg_pressure_sac, in bar/min. Average pressure-based surface air consumption */
    public var avgPressureSac: Double?
        get() = getFieldValue(AVG_PRESSURE_SAC_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_PRESSURE_SAC_FIELD_NUM, value)

    /** avg_volume_sac, in L/min. Average volumetric surface air consumption */
    public var avgVolumeSac: Double?
        get() = getFieldValue(AVG_VOLUME_SAC_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_VOLUME_SAC_FIELD_NUM, value)

    /** avg_rmv, in L/min. Average respiratory minute volume */
    public var avgRmv: Double?
        get() = getFieldValue(AVG_RMV_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_RMV_FIELD_NUM, value)

    /** descent_time, in s. Time to reach deepest level stop */
    public var descentTime: Double?
        get() = getFieldValue(DESCENT_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(DESCENT_TIME_FIELD_NUM, value)

    /** ascent_time, in s. Time after leaving bottom until reaching surface */
    public var ascentTime: Double?
        get() = getFieldValue(ASCENT_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(ASCENT_TIME_FIELD_NUM, value)

    /** avg_ascent_rate, in m/s. Average ascent rate, not including descents or stops */
    public var avgAscentRate: Double?
        get() = getFieldValue(AVG_ASCENT_RATE_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_ASCENT_RATE_FIELD_NUM, value)

    /** avg_descent_rate, in m/s. Average descent rate, not including ascents or stops */
    public var avgDescentRate: Double?
        get() = getFieldValue(AVG_DESCENT_RATE_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_DESCENT_RATE_FIELD_NUM, value)

    /** max_ascent_rate, in m/s. Maximum ascent rate */
    public var maxAscentRate: Double?
        get() = getFieldValue(MAX_ASCENT_RATE_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_ASCENT_RATE_FIELD_NUM, value)

    /** max_descent_rate, in m/s. Maximum descent rate */
    public var maxDescentRate: Double?
        get() = getFieldValue(MAX_DESCENT_RATE_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_DESCENT_RATE_FIELD_NUM, value)

    /** hang_time, in s. Time spent neither ascending nor descending */
    public var hangTime: Double?
        get() = getFieldValue(HANG_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(HANG_TIME_FIELD_NUM, value)

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val REFERENCE_MESG_FIELD_NUM: UByte = 0u
        public const val REFERENCE_INDEX_FIELD_NUM: UByte = 1u
        public const val AVG_DEPTH_FIELD_NUM: UByte = 2u
        public const val MAX_DEPTH_FIELD_NUM: UByte = 3u
        public const val SURFACE_INTERVAL_FIELD_NUM: UByte = 4u
        public const val START_CNS_FIELD_NUM: UByte = 5u
        public const val END_CNS_FIELD_NUM: UByte = 6u
        public const val START_N2_FIELD_NUM: UByte = 7u
        public const val END_N2_FIELD_NUM: UByte = 8u
        public const val O2_TOXICITY_FIELD_NUM: UByte = 9u
        public const val DIVE_NUMBER_FIELD_NUM: UByte = 10u
        public const val BOTTOM_TIME_FIELD_NUM: UByte = 11u
        public const val AVG_PRESSURE_SAC_FIELD_NUM: UByte = 12u
        public const val AVG_VOLUME_SAC_FIELD_NUM: UByte = 13u
        public const val AVG_RMV_FIELD_NUM: UByte = 14u
        public const val DESCENT_TIME_FIELD_NUM: UByte = 15u
        public const val ASCENT_TIME_FIELD_NUM: UByte = 16u
        public const val AVG_ASCENT_RATE_FIELD_NUM: UByte = 17u
        public const val AVG_DESCENT_RATE_FIELD_NUM: UByte = 22u
        public const val MAX_ASCENT_RATE_FIELD_NUM: UByte = 23u
        public const val MAX_DESCENT_RATE_FIELD_NUM: UByte = 24u
        public const val HANG_TIME_FIELD_NUM: UByte = 25u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("ReferenceMesg", 0u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[1] = Field("ReferenceIndex", 1u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[2] = Field("AvgDepth", 2u, BaseType.UINT32, 1000.0, 0.0, "m", false)
            fields[3] = Field("MaxDepth", 3u, BaseType.UINT32, 1000.0, 0.0, "m", false)
            fields[4] = Field("SurfaceInterval", 4u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[5] = Field("StartCns", 5u, BaseType.UINT8, 1.0, 0.0, "percent", false)
            fields[6] = Field("EndCns", 6u, BaseType.UINT8, 1.0, 0.0, "percent", false)
            fields[7] = Field("StartN2", 7u, BaseType.UINT16, 1.0, 0.0, "percent", false)
            fields[8] = Field("EndN2", 8u, BaseType.UINT16, 1.0, 0.0, "percent", false)
            fields[9] = Field("O2Toxicity", 9u, BaseType.UINT16, 1.0, 0.0, "OTUs", false)
            fields[10] = Field("DiveNumber", 10u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[11] = Field("BottomTime", 11u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[12] = Field("AvgPressureSac", 12u, BaseType.UINT16, 100.0, 0.0, "bar/min", false)
            fields[13] = Field("AvgVolumeSac", 13u, BaseType.UINT16, 100.0, 0.0, "L/min", false)
            fields[14] = Field("AvgRmv", 14u, BaseType.UINT16, 100.0, 0.0, "L/min", false)
            fields[15] = Field("DescentTime", 15u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[16] = Field("AscentTime", 16u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[17] = Field("AvgAscentRate", 17u, BaseType.SINT32, 1000.0, 0.0, "m/s", false)
            fields[22] = Field("AvgDescentRate", 22u, BaseType.UINT32, 1000.0, 0.0, "m/s", false)
            fields[23] = Field("MaxAscentRate", 23u, BaseType.UINT32, 1000.0, 0.0, "m/s", false)
            fields[24] = Field("MaxDescentRate", 24u, BaseType.UINT32, 1000.0, 0.0, "m/s", false)
            fields[25] = Field("HangTime", 25u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            return fields
        }
    }
}
